import { useState, type ReactNode } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { Dialog } from '@/components/ui/dialog';
import { XStack, YStack } from '@/components/ui/layout/layout';

import { TeamRoleEnum, type TeamMember } from '../team.types';

import { InviteMemberModal } from './InviteMemberModal';

interface InviteMemberActionProps {
  teamId: string;
  members: TeamMember[];
  isAdmin: boolean;
  children: (props: { onClick: () => void }) => ReactNode;
}

export function InviteMemberAction({
  teamId,
  members,
  isAdmin,
  children,
}: InviteMemberActionProps) {
  const [showInvite, setShowInvite] = useState(false);
  const [showAdminHint, setShowAdminHint] = useState(false);

  const admins = members.filter(
    (m) =>
      m.role === TeamRoleEnum.enum.owner || m.role === TeamRoleEnum.enum.admin
  );

  const handleClick = () => {
    if (isAdmin) {
      setShowInvite(true);
    } else {
      setShowAdminHint(true);
    }
  };

  return (
    <>
      {children({ onClick: handleClick })}

      <InviteMemberModal
        teamId={teamId}
        open={showInvite}
        onOpenChange={setShowInvite}
      />

      <Dialog.Root open={showAdminHint} onOpenChange={setShowAdminHint}>
        <Dialog.Content className="max-w-sm" showCloseButton={false}>
          <Dialog.Header>
            <Dialog.Title>Need to add someone?</Dialog.Title>
            <Dialog.Description>
              Only admins can invite new members. Reach out to:
            </Dialog.Description>
          </Dialog.Header>
          <YStack className="gap-2">
            {admins.map((admin) => (
              <XStack
                key={admin.id}
                className="items-center gap-2 rounded-lg border border-border px-3 py-2"
              >
                <Icons.user className="size-3.5 text-foreground-muted shrink-0" />
                <YStack className="gap-0 min-w-0">
                  {admin.name && (
                    <span className="text-sm font-medium text-foreground truncate">
                      {admin.name}
                    </span>
                  )}
                  <span className="text-xs text-foreground-muted truncate">
                    {admin.email}
                  </span>
                </YStack>
              </XStack>
            ))}
          </YStack>
          <Dialog.Footer>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdminHint(false)}
            >
              Got it
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Root>
    </>
  );
}
